import {
  createContext,
  PropsWithChildren,
  useCallback,
  useMemo,
  useState,
} from "react";
import omit from "lodash/omit";
import { mergeDeep, TRecursivePartial } from "utils/helpers";
import { BackgroundJobType } from "./BackgroundJobsContext.constants";
import {
  TBackgroundJobsContextValue,
  TPossibleBackgroundJob,
} from "./BackgroundJobsContext.types";

export const BackgroundJobsContext = createContext<
  TBackgroundJobsContextValue | undefined
>(undefined);

type TJobsState = {
  [T in BackgroundJobType]: {
    [key: string]: Extract<TPossibleBackgroundJob, { type: T }>;
  };
};

export const BackgroundJobsProvider = ({ children }: PropsWithChildren) => {
  const [jobs, setJobs] = useState<TJobsState>({
    [BackgroundJobType.FileUpload]: {},
    [BackgroundJobType.ProjectInvitation]: {},
    [BackgroundJobType.StatusChange]: {},
    [BackgroundJobType.Submit]: {},
  });

  const addJob = useCallback(
    (job: TPossibleBackgroundJob) => {
      setJobs((jobs) => ({
        ...jobs,
        [job.type]: {
          ...jobs[job.type],
          [job.id]: job,
        },
      }));
    },
    [setJobs],
  );

  const getJob = useCallback(
    <T extends BackgroundJobType>(jobId: string | undefined, jobType: T) => {
      if (!jobId) {
        return undefined;
      }

      return jobs[jobType][jobId];
    },
    [jobs],
  );

  const updateJobData = useCallback(
    <T extends TPossibleBackgroundJob>(
      jobId: string,
      jobType: T["type"],
      jobData: TRecursivePartial<T["data"]>,
    ) => {
      setJobs((jobs) => ({
        ...jobs,
        [jobType]: {
          ...jobs[jobType],
          [jobId]: {
            ...jobs[jobType][jobId],
            data: mergeDeep(jobs[jobType][jobId].data, jobData),
          },
        },
      }));
    },
    [setJobs],
  );

  const removeJob = useCallback(
    (jobId: string, jobType: BackgroundJobType) => {
      setJobs((jobs) => ({
        ...jobs,
        [jobType]: omit(jobs[jobType], [jobId]),
      }));
    },
    [setJobs],
  );

  const value = useMemo(
    () => ({ addJob, getJob, updateJobData, removeJob }),
    [addJob, getJob, updateJobData, removeJob],
  );

  return (
    <BackgroundJobsContext.Provider value={value}>
      {children}
    </BackgroundJobsContext.Provider>
  );
};
